import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  FileText, Sparkles, Download, Globe, Shield, Zap,
  CheckCircle, ArrowRight, Users, BarChart3, Layers,
  ChevronDown, ChevronLeft, ChevronRight
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { sampleResumes } from "@/data/sampleResumes";
import { ResumePreview } from "@/components/resume/ResumePreview";
import type { TemplateId } from "@/types/resume";
import Testimonials from "@/components/landing/Testimonials";
import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { SocialProofBar } from "@/components/SocialProofBar";

const Index = () => {
  const [showcaseIdx, setShowcaseIdx] = useState(0);
  const currentSample = sampleResumes[showcaseIdx];

  const faqItems = [
    { q: "Is this a free AI resume builder?", a: "Yes — AI Resume Studio is a free AI resume builder. You can create, preview, and export ATS-friendly resumes as PDF with no watermark, no credit card, and no hidden fees." },
    { q: "Does it include an AI cover letter generator?", a: "Yes. Every account includes the AI cover letter generator — create custom, job-specific cover letters in seconds, with multiple tones and instant PDF export." },
    { q: "Are the resumes ATS-friendly?", a: "Yes! All templates are designed with ATS compatibility in mind. Clean text structure, proper headings, and standard formatting ensure your resume gets parsed correctly." },
    { q: "How does AI-assisted mode work?", a: "You provide your career details, target role, and key highlights. Our AI generates professional summaries, bullet points, and suggestions tailored to your target position. You can edit everything before exporting." },
    { q: "Can I create multiple resumes?", a: "Absolutely. Create different versions for different roles — label them, duplicate them, and manage them all from your dashboard." },
    { q: "How does the shareable web link work?", a: "Each resume can be published as a live web page with a unique URL. You control visibility and can regenerate the link at any time." },
    { q: "What formats are supported for export?", a: "Currently we support PDF export in A4 format. The live web resume serves as your interactive online version." },
    { q: "Is this also a CV builder online?", a: "Yes. \"Resume\" and \"CV\" mean the same document in most markets, and AI Resume Studio works as a free CV builder online with the same professional resume templates, AI assistance, and free PDF export." },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        "name": "AI Resume Studio",
        "url": "https://airesumestudio.net",
        "description": "Free AI resume builder and ATS resume maker. Create ATS-friendly resumes, professional CVs, and AI cover letters online — export PDF free, with no watermark.",
        "keywords": "AI resume builder, free AI resume builder, ATS resume builder, AI cover letter generator, online resume builder, professional CV maker, best resume builder 2026",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
      },
      {
        "@type": "SoftwareApplication",
        "name": "AI Resume Studio",
        "url": "https://airesumestudio.net",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "featureList": [
          "ATS-friendly resume templates",
          "AI-generated professional summaries",
          "AI-generated experience bullet points",
          "AI cover letter generator",
          "Free PDF export with no watermark",
          "Shareable public web resume link",
          "5 professional resume templates",
          "Profile photo support",
          "Multiple resume management"
        ]
      },
      {
        "@type": "Organization",
        "name": "AI Resume Studio",
        "url": "https://airesumestudio.net",
        "logo": "https://airesumestudio.net/favicon.png"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqItems.map(item => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": { "@type": "Answer", "text": item.a }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">

      <SiteHeader />

      <main>
      {/* Hero — static markup for fastest LCP, no JS-driven animation */}
      <section className="relative overflow-hidden py-20 md:py-28 lg:py-32 aurora-bg">
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-card border border-border shadow-xs text-primary text-sm font-medium mb-7">
              <Sparkles className="w-4 h-4 shrink-0" /> Free AI Resume Builder · ATS-Friendly
            </div>
            <h1 className="font-display text-display-lg sm:text-display-xl font-bold text-foreground max-w-4xl mx-auto mb-6">
              Free Online Resume Builder - Create a Professional Resume{" "}
              <span className="gradient-text">in Minutes</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-2xl mx-auto">
              The free resume maker and CV builder online trusted by job seekers in 2026. Pick from professional resume templates, generate ATS-optimized content with our AI resume writer, add an AI cover letter, and export a watermark-free PDF in minutes.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
              <Button
                size="lg"
                className="w-full sm:w-auto gap-2 text-base px-8 shadow-primary transition-transform duration-180 ease-premium hover:-translate-y-0.5"
                asChild
              >
                <Link to="/signup" title="Sign up free and start building your professional resume">
                  Build My Resume Free <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto gap-2 text-base px-8 border-border-strong bg-card hover:bg-secondary hover:text-foreground transition-colors duration-180 ease-premium"
                asChild
              >
                <a href="#templates" title="Preview our professional resume templates">
                  See Example Resume
                </a>
              </Button>
            </div>
            <SocialProofBar className="mt-10" />
          </div>
        </div>
      </section>

      {/* Mode Explanation */}
      <section className="py-16 md:py-24 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
            <h2 className="font-display text-display-md font-bold text-foreground mb-4">Two Ways to Build Your Resume with Our Free Resume Maker</h2>
            <p className="text-lg text-muted-foreground">Choose the approach that suits your style</p>
          </div>
          <div className="grid md:grid-cols-2 items-stretch gap-5 md:gap-6 max-w-4xl mx-auto">
            <Card className="relative h-full overflow-hidden border-border bg-card shadow-xs transition-[transform,box-shadow,border-color] duration-220 ease-premium hover:-translate-y-0.5 hover:shadow-md hover:border-border-strong focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <CardContent className="flex h-full flex-col p-6 md:p-8">
                <div className="w-10 h-10 rounded-md bg-secondary text-foreground flex items-center justify-center mb-5">
                  <FileText className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl font-bold text-foreground mb-2">Manual Resume</h3>
                <p className="text-muted-foreground leading-relaxed">Full control over every word. Write your resume section by section with guided structure, inline hints, and formatting help.</p>
                <ul className="mt-auto pt-6 space-y-2.5 text-sm text-muted-foreground">
                  {['Complete creative control', 'Structured step-by-step flow', 'Real-time preview'].map(t => (
                    <li key={t} className="flex items-start gap-2.5"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-success" />{t}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="relative h-full overflow-hidden border-primary/30 bg-card shadow-sm transition-[transform,box-shadow,border-color] duration-220 ease-premium hover:-translate-y-0.5 hover:shadow-md hover:border-primary/50 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <div className="absolute top-5 right-5 px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-md">Popular</div>
              <CardContent className="flex h-full flex-col p-6 md:p-8">
                <div className="w-10 h-10 rounded-md bg-primary-soft text-primary flex items-center justify-center mb-5">
                  <Sparkles className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl font-bold text-foreground mb-2 pr-20">AI-Assisted Resume</h3>
                <p className="text-muted-foreground leading-relaxed">Provide your career details and let AI generate professional content tailored to your target role.</p>
                <ul className="mt-auto pt-6 space-y-2.5 text-sm text-muted-foreground">
                  {['AI-generated summaries & bullets', 'ATS keyword optimization', 'Tailor to job descriptions'].map(t => (
                    <li key={t} className="flex items-start gap-2.5"><CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-success" />{t}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
            <h2 className="font-display text-display-md font-bold text-foreground mb-4">Resume Builder Features — Everything You Need to Land the Job</h2>
            <p className="text-lg text-muted-foreground">Professional resume templates, an AI resume writer, ATS scoring, and an AI cover letter generator — all free.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 items-stretch gap-5 md:gap-6 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: "ATS-Friendly Templates", desc: "ATS-optimized templates designed to pass applicant tracking systems" },
              { icon: Download, title: "Free PDF Export, No Watermark", desc: "Download polished A4 PDFs ready for submission — free, no watermark" },
              { icon: Globe, title: "Live Web Resume", desc: "Share a unique link to your interactive online resume" },
              { icon: Sparkles, title: "AI Resume Writing Tool", desc: "Generate professional summaries, bullet points, and skills with AI" },
              { icon: Layers, title: "5 Professional CV Templates", desc: "From ATS Classic to Elegant Two-Column" },
              { icon: Zap, title: "Real-Time Preview", desc: "See changes instantly as you type" },
              { icon: Users, title: "Multiple Resumes & CVs", desc: "Create versions for different job applications" },
              { icon: BarChart3, title: "ATS Resume Score", desc: "Get completeness and ATS-compatibility feedback" },
              { icon: CheckCircle, title: "AI Cover Letter Generator", desc: "Built-in AI cover letter generator for every job application" },
            ].map((f) => (
              <div key={f.title} className="h-full">
                <Card className="h-full border-border bg-card shadow-xs transition-[transform,box-shadow,border-color] duration-220 ease-premium hover:-translate-y-0.5 hover:shadow-md hover:border-border-strong">
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="w-10 h-10 rounded-md bg-primary-soft text-primary flex items-center justify-center mb-4">
                      <f.icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-sans text-base font-semibold text-foreground mb-1.5">{f.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
            <h2 className="font-display text-display-md font-bold text-foreground mb-4">How Our Free Resume Maker Works in 4 Steps</h2>
            <p className="text-lg text-muted-foreground">From signup to share in four simple steps</p>
          </div>
          <div className="relative max-w-5xl mx-auto">
            {/* Sequence connector — desktop only, purely decorative */}
            <div
              aria-hidden="true"
              className="hidden lg:block absolute left-[12.5%] right-[12.5%] top-7 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
            />
            <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-x-8 sm:gap-y-12">
              {[
                { step: "1", title: "Choose Mode", desc: "Pick Manual or AI-Assisted to match your workflow" },
                { step: "2", title: "Fill Details", desc: "Enter your information using our guided builder" },
                { step: "3", title: "Pick Template", desc: "Select a professional template that fits your style" },
                { step: "4", title: "Export & Share", desc: "Download PDF or generate a shareable web link" },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="relative z-10 w-14 h-14 rounded-full bg-card border border-border shadow-sm ring-4 ring-surface-sunken flex items-center justify-center text-primary font-display font-bold text-xl tabular mx-auto mb-5">{s.step}</div>
                  <h3 className="font-sans text-base font-semibold text-foreground mb-1.5">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-[24ch] mx-auto">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="templates" className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-10 md:mb-12">
            <h2 className="font-display text-display-md font-bold text-foreground mb-4">Professional Resume Templates — Modern, Classic &amp; ATS-Friendly</h2>
            <p className="text-lg text-muted-foreground">ATS-friendly professional resume templates that get you noticed — previewed with real resume data.</p>
          </div>

          {/* Template selector pills */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-10">
            {sampleResumes.map((sample, idx) => (
              <button
                key={sample.name}
                onClick={() => setShowcaseIdx(idx)}
                className={`inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md border text-sm font-medium transition-colors duration-180 ease-premium ${
                  showcaseIdx === idx
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-border-strong"
                }`}
              >
                {sample.role}
              </button>
            ))}
          </div>

          {/* Live preview card */}
          <div className="max-w-3xl mx-auto">
            <Card className="overflow-hidden rounded-xl border-border shadow-lg">
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-surface-sunken border-b border-border">
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-2.5 h-2.5 rounded-full bg-destructive/40" />
                  <div className="w-2.5 h-2.5 rounded-full bg-warning/40" />
                  <div className="w-2.5 h-2.5 rounded-full bg-success/40" />
                </div>
                <span className="min-w-0 flex-1 text-center text-xs text-muted-foreground font-medium">
                  {currentSample.name} — {currentSample.template.replace("-", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    aria-label="Previous template"
                    onClick={() => setShowcaseIdx((prev) => (prev - 1 + sampleResumes.length) % sampleResumes.length)}
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    aria-label="Next template"
                    onClick={() => setShowcaseIdx((prev) => (prev + 1) % sampleResumes.length)}
                  >
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="max-h-[520px] md:max-h-[640px] overflow-auto bg-white light text-black">
                <div className="light" style={{ colorScheme: 'light' }}>
                  <ResumePreview data={currentSample.data} template={currentSample.template as TemplateId} />
                </div>
              </div>
            </Card>
            <div className="text-center mt-8">
              <Button size="lg" className="gap-2 px-8 shadow-primary transition-transform duration-180 ease-premium hover:-translate-y-0.5" asChild>
                <Link to="/signup">Build Yours Now <ArrowRight className="w-4 h-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <div className="rule-fade" />
      </div>

      <Testimonials />

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24 bg-surface-sunken">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-display text-display-md font-bold text-foreground mb-4">Resume Builder FAQ — Free Resume Maker Questions Answered</h2>
          </div>
           <Accordion type="single" collapsible className="space-y-3">
            {faqItems.map((item) => (
              <AccordionItem key={item.q} value={item.q} className="bg-card rounded-lg border border-border shadow-xs px-5 sm:px-6 transition-colors duration-180 ease-premium hover:border-border-strong">
                <AccordionTrigger className="text-left font-sans font-medium text-foreground py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Internal links strip — keyword-rich anchors for SEO and discovery */}
      <section className="py-16 md:py-20" aria-labelledby="explore-heading">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="rounded-xl border border-border bg-card shadow-xs p-8 md:p-10 text-center">
            <h2 id="explore-heading" className="font-display text-display-sm font-bold text-foreground mb-4">
              Explore the rest of AI Resume Studio
            </h2>
            <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Browse our{" "}
              <Link to="/resume-templates" className="font-medium text-primary underline-offset-4 hover:underline" title="Professional resume templates by industry">
                professional resume templates gallery
              </Link>
              , see real{" "}
              <Link to="/resume-examples" className="font-medium text-primary underline-offset-4 hover:underline" title="Resume examples by job title">
                resume examples by job title
              </Link>
              , read the{" "}
              <Link to="/guides/how-to-write-a-resume" className="font-medium text-primary underline-offset-4 hover:underline" title="The complete 2026 guide to writing a resume">
                complete 2026 resume writing guide
              </Link>
              , or jump straight into the{" "}
              <Link to="/ats-resume-builder" className="font-medium text-primary underline-offset-4 hover:underline" title="ATS-friendly resume builder">
                ATS-friendly resume builder
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden max-w-3xl mx-auto text-center gradient-primary rounded-xl shadow-lg px-6 py-12 md:px-12 md:py-16">
            <h2 className="font-display text-display-sm font-bold text-primary-foreground mb-4">Ready to Build Your Professional Resume?</h2>
            <p className="text-primary-foreground/85 text-lg leading-relaxed mb-8 max-w-xl mx-auto">Join thousands of job seekers who've landed interviews with our free resume builder.</p>
            <Button
              size="lg"
              variant="secondary"
              className="w-full sm:w-auto gap-2 text-base px-8 bg-card text-foreground hover:bg-card/90 shadow-md transition-transform duration-180 ease-premium hover:-translate-y-0.5"
              asChild
            >
              <Link to="/signup" title="Create your free professional resume now">
                Build My Resume Free <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Index;
